import * as THREE from "three";
import { defineModel, type Part, type Vec3 } from "../../lib";

// Porsche 911 — the classic air-cooled coupe at true scale (1 unit = 1 metre):
// length ~4.25, width 1.65 (rear haunches flare wider), height ~1.27,
// wheelbase 2.27. Nose is +Z. Authored for the `cars` showcase batch. The
// recognisability cues: a cab-forward profile (the cowl sits near the front
// axle over a SHORT trunk lid dipping between high fenders), upright round
// headlamp pods standing on the front fender crowns, a grille-less smooth
// nose (the engine is in the back), one unbroken fastback line from the roof
// peak to a low tail (three tilted boxes overlapping edge-to-edge, each next
// one shallower), engine-lid louvres lying flat on that slope right behind
// the rear window, a wide wrap-around tail lamp bar joined by a reflector
// strip, flared rear haunches and five-petal Fuchs-style wheels. Body panels
// carry the "paint" recolour slot (factory Guards Red); `drive` spins the
// wheels and `hazards` blinks four indicators.

const PAINT = "#b02525"; // factory Guards Red — the "paint" slot default
const TRIM = "#17181b"; // gloss-black trim (louvres, pillars, horn grilles)
const CLAD = "#26282c"; // dark valances under the bumpers
const WELL = "#141518"; // wheel-well discs
const GLASS = "#161c22"; // near-black side / rear glazing over the interior
const SCREEN = "#1f2933"; // windscreen (a shade lighter than the tint)
const CABIN = "#101114"; // interior block behind the glass
const CHROME = "#ccd1d6";
const PETAL = "#c4c9cf"; // bright Fuchs petals
const DISH = "#101114"; // dark wheel dish behind the petals
const TIRE = "#1a1b1d";
const BRAKE = "#8c9196";
const LAMP = "#e8f0f4"; // headlamp glass
const AMBER = "#e8a13a";
const TAIL_RED = "#8f1220"; // tail lamps, deeper than the paint so they read
const REFLECTOR = "#701020";
const PLATE = "#eceff2";

// Spec-sheet skeleton (metres).
const WHEEL_R = 0.325;
const AXLE_F = 1.15;
const AXLE_R = -1.12; // wheelbase 2.27
const WHEEL_X_F = 0.77; // half track, front — tyre faces proud of the body
const WHEEL_X_R = 0.82; // half track, rear (under the flared haunches)

// The unbroken roof-to-tail line: three tilted boxes whose top surfaces meet
// on the same breakpoints and OVERLAP slightly (each box runs a little past
// its span, tucking under the neighbour) so no seam can open up.
// Top-surface breakpoints: (-0.10, 1.25) -> (-0.70, 0.995) -> (-1.45, 0.905)
// -> (-1.98, 0.855). Angles are atan(drop / run) of each span — the first
// (rear window) drop is steep, then the line flattens onto the tail.
const SAIL_ANGLE = 0.4021;
const LID_ANGLE = 0.1194;
const DECK_ANGLE = 0.0941;
const SCREEN_RAKE = 0.547; // classic 911 windscreen
const BONNET_SLOPE = 0.1329; // short trunk lid diving between the fenders
const FENDER_SLOPE = 0.049; // fender crowns stay high out to the lamp pods
const NOSE_ROLL = 0.514; // the lid rolling down to the bumper at the nose
const FENDER_ROLL = 0.534; // the fender tips rolling down beside it

function box(
  w: number, h: number, d: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.BoxGeometry(w, h, d), color, position, rotation, ...extra };
}

function cyl(
  rTop: number, rBot: number, h: number, seg: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.CylinderGeometry(rTop, rBot, h, seg), color, position, rotation, ...extra };
}

/** A painted body panel — carries the "paint" recolour slot. */
function paint(w: number, h: number, d: number, position: Vec3, rotation?: Vec3): Part {
  return box(w, h, d, PAINT, position, rotation, { slot: "paint" });
}

/** One Fuchs-style wheel: tyre, brake disc, dark recessed dish, five bright
 *  petals slightly proud of the tyre face, chrome centre cap. Every part
 *  shares `name` so the `drive` track spins them as one node about the axle.
 *  `side` is +1 for the right (+x) wheel, -1 for the left. */
function wheel(side: 1 | -1, halfTrack: number, tyreW: number, z: number, name: string): Part[] {
  const y = WHEEL_R;
  const outer = halfTrack + tyreW / 2; // tyre outer face — must stay proud of the well disc
  const parts: Part[] = [
    cyl(WHEEL_R, WHEEL_R, tyreW, 18, TIRE, [side * halfTrack, y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.14, 0.14, 0.03, 14, BRAKE, [side * (halfTrack - 0.05), y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.185, 0.185, 0.05, 18, DISH, [side * (outer - 0.055), y, z], [0, 0, Math.PI / 2], { name }),
  ];
  for (let k = 0; k < 5; k++) {
    parts.push(
      box(0.06, 0.3, 0.09, PETAL, [side * (outer - 0.02), y, z], [(k * 2 * Math.PI) / 5, 0, 0], { name }),
    );
  }
  parts.push(cyl(0.045, 0.045, 0.028, 10, CHROME, [side * (outer - 0.002), y, z], [0, 0, Math.PI / 2], { name }));
  return parts;
}

/** The opened wheel well: a dark disc proud of the body side (the arch "cut")
 *  plus a body-coloured half-torus brow over it — the 911's arch is a painted
 *  metal flare, not black cladding. The [0, pi/2, 0] yaw puts the 0..pi arc
 *  exactly over the top half. */
function arch(side: 1 | -1, z: number, discX: number, discR: number, browR: number, browTube: number): Part[] {
  return [
    cyl(discR, discR, 0.04, 18, WELL, [side * discX, WHEEL_R, z], [0, 0, Math.PI / 2]),
    {
      geometry: new THREE.TorusGeometry(browR, browTube, 6, 18, Math.PI),
      color: PAINT,
      position: [side * (discX + 0.015), WHEEL_R, z],
      rotation: [0, Math.PI / 2, 0],
      slot: "paint",
    },
  ];
}

// The four hazard indicators — parts and blink tracks are generated from one
// table so the pivots (each part's own centre) can never drift from the parts.
// Front pair sits under the headlamps on the nose panel; rear pair is the
// amber outer section of each tail lamp.
const INDICATORS: ReadonlyArray<{ name: string; pos: Vec3; size: Vec3 }> = [
  { name: "indicator-fl", pos: [-0.58, 0.46, 2.08], size: [0.2, 0.055, 0.04] },
  { name: "indicator-fr", pos: [0.58, 0.46, 2.08], size: [0.2, 0.055, 0.04] },
  { name: "indicator-rl", pos: [-0.64, 0.75, -2.11], size: [0.12, 0.09, 0.03] },
  { name: "indicator-rr", pos: [0.64, 0.75, -2.11], size: [0.12, 0.09, 0.03] },
];

function parts(): Part[] {
  const P: Part[] = [];
  const S = [1, -1] as const;

  // -- Running gear ----------------------------------------------------------
  P.push(
    ...wheel(-1, WHEEL_X_F, 0.22, AXLE_F, "wheel-fl"), ...wheel(1, WHEEL_X_F, 0.22, AXLE_F, "wheel-fr"),
    ...wheel(-1, WHEEL_X_R, 0.25, AXLE_R, "wheel-rl"), ...wheel(1, WHEEL_X_R, 0.25, AXLE_R, "wheel-rr"),
  );
  for (const z of [AXLE_F, AXLE_R]) {
    P.push(cyl(0.05, 0.05, 1.4, 8, TRIM, [0, WHEEL_R, z], [0, 0, Math.PI / 2]));
  }
  for (const s of S) {
    P.push(...arch(s, AXLE_F, 0.84, 0.4, 0.37, 0.05)); // front arch
    P.push(...arch(s, AXLE_R, 0.9, 0.42, 0.395, 0.045)); // flared rear arch, clearly wider
  }
  P.push(box(1.44, 0.1, 3.3, TRIM, [0, 0.15, 0])); // underbody pan

  // -- Body masses (paint) ----------------------------------------------------
  P.push(paint(1.62, 0.44, 3.9, [0, 0.38, 0])); // main tub, sill to fender line
  P.push(paint(1.56, 0.16, 2.84, [0, 0.68, -0.53])); // upper band, cowl to tail
  P.push(paint(1.5, 0.05, 0.14, [0, 0.76, 0.82])); // cowl below the windscreen
  P.push(paint(0.86, 0.1, 1.01, [0, 0.6425, 1.355], [BONNET_SLOPE, 0, 0])); // short trunk lid between the fenders
  for (const s of S) P.push(paint(0.38, 0.17, 1.0, [s * 0.61, 0.665, 1.34], [FENDER_SLOPE, 0, 0])); // high fender crowns
  P.push(paint(0.86, 0.1, 0.26, [0, 0.516, 1.95], [NOSE_ROLL, 0, 0])); // lid rolling down to the bumper
  for (const s of S) P.push(paint(0.38, 0.1, 0.27, [s * 0.61, 0.615, 1.98], [FENDER_ROLL, 0, 0])); // fender tips rolling down
  P.push(paint(1.58, 0.2, 0.16, [0, 0.4, 1.99])); // smooth nose panel (no grille)
  P.push(paint(1.52, 0.46, 0.14, [0, 0.615, -1.99])); // low tail panel
  for (const s of S) P.push(paint(0.13, 0.36, 1.55, [s * 0.805, 0.58, -1.12])); // rear haunch flare

  // -- The unbroken fastback: sail + engine-lid top + tail deck ---------------
  // Each box is slightly longer than its top-surface span so the ends tuck
  // under the neighbouring segment — overlapped, never butted.
  P.push(paint(1.38, 0.44, 0.72, [0, 0.92, -0.314], [-SAIL_ANGLE, 0, 0])); // sail: the steep rear-window drop
  P.push(paint(1.48, 0.3, 0.82, [0, 0.801, -1.057], [-LID_ANGLE, 0, 0])); // engine-lid top
  P.push(paint(1.56, 0.2, 0.6, [0, 0.78, -1.706], [-DECK_ANGLE, 0, 0])); // tail deck, ends low over the tail
  for (let i = 0; i < 5; i++) {
    // engine-lid louvres laid flat on the slope, right behind the window
    const z = -0.8 - 0.1 * i;
    const y = 0.995 + 0.12 * (z + 0.7) + 0.013;
    P.push(box(0.94, 0.02, 0.06, TRIM, [0, y, z], [-LID_ANGLE, 0, 0]));
  }
  P.push(cyl(0.03, 0.03, 0.016, 12, CHROME, [0, 0.898, -1.62], [-DECK_ANGLE, 0, 0])); // deck badge disc

  // -- Greenhouse (cowl near the front axle — the cab-forward look) -----------
  P.push(box(1.28, 0.54, 0.045, SCREEN, [0, 0.99, 0.71], [-SCREEN_RAKE, 0, 0])); // windscreen
  // A-pillars stay short and inboard of the roof edge — nothing may stand
  // above or beside the roofline.
  for (const s of S) P.push(paint(0.11, 0.47, 0.08, [s * 0.6, 0.96, 0.745], [-SCREEN_RAKE, 0, 0]));
  P.push(paint(1.32, 0.07, 0.73, [0, 1.235, 0.255])); // roof, peak of the car
  P.push(box(1.26, 0.28, 0.9, CABIN, [0, 0.93, 0.17])); // black interior block
  for (const s of S) {
    P.push(box(0.035, 0.46, 0.78, GLASS, [s * 0.7, 0.99, 0.31], [0, 0, s * 0.3])); // door glass
    P.push(box(0.05, 0.42, 0.07, TRIM, [s * 0.7, 0.96, -0.09], [0, 0, s * 0.3])); // B-pillar
    P.push(box(0.035, 0.26, 0.4, GLASS, [s * 0.705, 0.92, -0.31], [0, 0, s * 0.3])); // quarter glass
  }
  P.push(box(1.06, 0.03, 0.52, GLASS, [0, 1.137, -0.418], [-SAIL_ANGLE, 0, 0])); // rear window on the sail

  // -- Front fascia: round lamp pods on the fender crowns ---------------------
  for (const s of S) {
    // bezel and glass are narrower than the pod so no trim peeks out sideways;
    // the pod sinks into the fender crown so it reads as a dome, not a stack
    P.push(cyl(0.12, 0.12, 0.34, 14, PAINT, [s * 0.6, 0.66, 1.82], [Math.PI / 2, 0, 0], { slot: "paint" })); // lamp pod
    P.push(cyl(0.1, 0.1, 0.022, 14, CHROME, [s * 0.6, 0.66, 2.001], [Math.PI / 2, 0, 0])); // chrome bezel
    P.push(cyl(0.085, 0.085, 0.018, 14, LAMP, [s * 0.6, 0.66, 2.016], [Math.PI / 2, 0, 0])); // lamp glass
    P.push(box(0.11, 0.05, 0.03, TRIM, [s * 0.3, 0.46, 2.075])); // horn grille
  }
  P.push(paint(1.58, 0.15, 0.12, [0, 0.38, 2.05])); // front bumper
  P.push(box(1.56, 0.045, 0.03, CHROME, [0, 0.43, 2.105])); // chrome bumper strip, inside the bumper ends
  P.push(box(0.44, 0.11, 0.025, PLATE, [0, 0.365, 2.105])); // low front plate, barely proud
  P.push(box(1.4, 0.18, 0.14, CLAD, [0, 0.27, 1.97])); // front valance
  P.push(cyl(0.035, 0.035, 0.016, 12, CHROME, [0, 0.652, 1.7], [BONNET_SLOPE, 0, 0])); // trunk badge disc

  // -- Rear fascia: tail lamp bar wrapping the corners on chrome bezels -------
  for (const s of S) {
    P.push(box(0.52, 0.16, 0.02, CHROME, [s * 0.52, 0.75, -2.065])); // bezel plate
    P.push(box(0.46, 0.13, 0.06, TAIL_RED, [s * 0.52, 0.75, -2.075])); // tail lamp bar
    P.push(box(0.055, 0.15, 0.22, TAIL_RED, [s * 0.775, 0.75, -1.975])); // corner wrap unit
  }
  P.push(box(0.6, 0.09, 0.05, REFLECTOR, [0, 0.75, -2.065])); // reflector strip joining the lamps
  P.push(box(0.44, 0.12, 0.025, PLATE, [0, 0.52, -2.075])); // rear plate
  P.push(paint(1.58, 0.15, 0.12, [0, 0.36, -2.06])); // rear bumper
  P.push(box(1.56, 0.045, 0.03, CHROME, [0, 0.41, -2.115])); // chrome bumper strip, inside the bumper ends
  P.push(box(1.4, 0.18, 0.14, CLAD, [0, 0.27, -1.98])); // rear valance

  // -- Furniture --------------------------------------------------------------
  for (const s of S) {
    P.push(box(0.06, 0.018, 0.025, TRIM, [s * 0.77, 0.8, 0.66])); // short dark mirror arm at the door top
    P.push(paint(0.09, 0.075, 0.05, [s * 0.825, 0.815, 0.64])); // small painted mirror head
    P.push(box(0.025, 0.025, 0.13, CHROME, [s * 0.785, 0.7, 0.05])); // door handle
    P.push(box(0.3, 0.012, 0.03, TRIM, [s * 0.26, 0.825, 0.83], [-SCREEN_RAKE, 0, 0])); // wiper on the glass
  }
  P.push(cyl(0.04, 0.04, 0.016, 10, CHROME, [0.61, 0.771, 1.0], [FENDER_SLOPE, 0, 0])); // fuel filler cap, right fender
  for (const ind of INDICATORS) {
    P.push(box(ind.size[0], ind.size[1], ind.size[2], AMBER, ind.pos, undefined, { name: ind.name }));
  }

  return P;
}

// One wheel revolution per cycle; positive x-rotation rolls the car toward +Z.
const WHEEL_PIVOTS: ReadonlyArray<{ name: string; pivot: Vec3 }> = [
  { name: "wheel-fl", pivot: [-WHEEL_X_F, WHEEL_R, AXLE_F] },
  { name: "wheel-fr", pivot: [WHEEL_X_F, WHEEL_R, AXLE_F] },
  { name: "wheel-rl", pivot: [-WHEEL_X_R, WHEEL_R, AXLE_R] },
  { name: "wheel-rr", pivot: [WHEEL_X_R, WHEEL_R, AXLE_R] },
];

export const porsche911 = defineModel({
  id: "porsche-911",
  name: "Porsche 911",
  batchId: "cars",
  tags: ["car", "porsche", "911", "coupe", "sports", "classic"],
  summary: "A classic air-cooled Porsche 911 coupe at true scale, in Guards Red.",
  description:
    "A classic air-cooled Porsche 911 coupe at real 1 unit = 1 metre scale " +
    "(about 4.25 long, 1.65 wide flaring wider over the rear haunches, " +
    "about 1.27 tall, 2.27 wheelbase). The signature cues are all present: " +
    "a cab-forward profile with a short trunk lid dipping between high " +
    "fenders, upright round chrome-ringed headlamp pods standing on the " +
    "fender crowns, a smooth grille-less nose with horn grilles and a low " +
    "plate, one unbroken fastback line from the roof peak to a low tail, " +
    "dark engine-lid louvres behind the rear window, a wide wrap-around " +
    "tail lamp bar joined by a reflector strip, chrome bumper strips and " +
    "five-petal Fuchs-style wheels. Body panels carry the 'paint' recolour " +
    "slot (factory Guards Red); `drive` spins the wheels and `hazards` " +
    "blinks the four indicators.",
  author: "Fable 5",
  footprint: 2.28,
  parts,
  animations: [
    {
      id: "drive",
      label: "Drive",
      duration: 1,
      tracks: WHEEL_PIVOTS.map(({ name, pivot }) => ({
        target: name,
        pivot,
        rotation: [
          { t: 0, value: [0, 0, 0] as Vec3 },
          { t: 0.5, value: [Math.PI, 0, 0] as Vec3 },
          { t: 1, value: [2 * Math.PI, 0, 0] as Vec3 },
        ],
      })),
    },
    {
      id: "hazards",
      label: "Hazard lights",
      duration: 1,
      tracks: INDICATORS.map(({ name, pos }) => ({
        target: name,
        pivot: pos,
        scale: [
          { t: 0, value: 1 },
          { t: 0.42, value: 1 },
          { t: 0.5, value: 0.02 },
          { t: 0.92, value: 0.02 },
          { t: 1, value: 1 },
        ],
      })),
    },
  ],
});
